import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  Tabs,
  Tab,
  IconButton,
  List,
  ListItem,
  FormHelperText
} from '@mui/material';
import {
  Subject as SubjectIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { fetchJson, resolveApiUrl } from '../../../utils/fetchJson';
import { usePhase } from '../../../contexts/PhaseContext';
import useScopeAutoRefresh from '../../../hooks/useScopeAutoRefresh';

const EMPTY_SUBJECT_FORM = {
  name: '',
  code: '',
  description: '',
  categoryId: '',
  coefficient: '',
  coefficientFirstCycle: '',
  coefficientSecondCycle: ''
};

const buildSubjectLevelKey = (name, level) => {
  const normalizedName = String(name || '').trim().toLowerCase();
  const normalizedLevel = String(level || '').trim().toLowerCase();
  if (!normalizedName) return '';
  return `${normalizedName}::${normalizedLevel}`;
};

const AddSubjectPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { activePhase, activePhaseId } = usePhase();
  
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoriesOptional, setCategoriesOptional] = useState(false);
  const [existingSubjects, setExistingSubjects] = useState([]);

  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [newCategoryLevel, setNewCategoryLevel] = useState('');
  const [editingCategoryId, setEditingCategoryId] = useState(null);

  const [selectedLevel, setSelectedLevel] = useState('');
  
  // Unified: multiple single-form rows
  const [subjectForms, setSubjectForms] = useState([{ ...EMPTY_SUBJECT_FORM }]);

  // Batch form data
  const [batchSubjects, setBatchSubjects] = useState([]);
  const [batchFormData, setBatchFormData] = useState({
    categoryId: '',
    coefficient: '',
    coefficientFirstCycle: '',
    coefficientSecondCycle: '',
    isActive: true
  });
  const [bulkFile, setBulkFile] = useState(null);

  const resolvedPhaseCode = useMemo(() => {
    const fromCtx = activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase');
    return fromCtx ? String(fromCtx).toLowerCase() : '';
  }, [activePhase]);

  const isSecPhase = resolvedPhaseCode === 'sec';

  const showFirstCycleCoefficient = isSecPhase && (!selectedLevel || String(selectedLevel) === 'first_cycle');
  const showSecondCycleCoefficient = isSecPhase && (!selectedLevel || String(selectedLevel) === 'second_cycle');
  const isNurseryLevel = String(selectedLevel || '') === 'nursery';

  const resolvedPhaseId = useMemo(() => {
    const fromCtx = activePhaseId || localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId');
    const parsed = parseInt(fromCtx, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }, [activePhaseId]);

  const coefficientRules = useMemo(() => {
    if (resolvedPhaseCode === 'sec') {
      return {
        title: t('subjectManagement.add.coefficientRules.secondary.title'),
        helper: t('subjectManagement.add.coefficientRules.secondary.helper'),
        bullets: [
          t('subjectManagement.add.coefficientRules.secondary.bullets.firstCycle'),
          t('subjectManagement.add.coefficientRules.secondary.bullets.secondCycle')
        ]
      };
    }
    if (resolvedPhaseCode === 'nur_pri') {
      return {
        title: t('subjectManagement.add.coefficientRules.nurseryPrimary.title'),
        helper: t('subjectManagement.add.coefficientRules.nurseryPrimary.helper'),
        bullets: [
          t('subjectManagement.add.coefficientRules.nurseryPrimary.bullets.nursery'),
          t('subjectManagement.add.coefficientRules.nurseryPrimary.bullets.primary')
        ]
      };
    }
    return {
      title: t('subjectManagement.add.coefficientRules.default.title'),
      helper: t('subjectManagement.add.coefficientRules.default.helper'),
      bullets: [t('subjectManagement.add.coefficientRules.default.bullets.autoOverrides')]
    };
  }, [resolvedPhaseCode, t]);

  useEffect(() => {
    const desired = location?.state?.activeTab;
    if (typeof desired === 'number' && desired !== activeTab) {
      setActiveTab(desired);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state]);

  useEffect(() => {
    const rawId = location?.state?.editCategoryId;
    if (rawId === null || rawId === undefined || rawId === '') return;
    const id = Number(rawId);
    if (!Number.isFinite(id) || id <= 0) return;
    setEditingCategoryId(id);
    if (activeTab !== 0) setActiveTab(0);
  }, [activeTab, location?.state]);

  useEffect(() => {
    if (!editingCategoryId) return;
    const cat = (categories || []).find(c => String(c?.id) === String(editingCategoryId));
    if (!cat) return;
    setNewCategoryName(cat.name || '');
    setNewCategoryDescription(cat.description || '');
    setNewCategoryLevel(cat.level || '');
  }, [categories, editingCategoryId]);

  const categoryLevelOptions = useMemo(() => {
    if (resolvedPhaseCode === 'sec') return ['first_cycle', 'second_cycle'];
    if (resolvedPhaseCode === 'nur_pri') return ['nursery', 'primary'];
    return ['nursery', 'primary', 'first_cycle', 'second_cycle'];
  }, [resolvedPhaseCode]);

  useEffect(() => {
    // Keep selected level valid per phase
    const options = Array.isArray(categoryLevelOptions) ? categoryLevelOptions : [];
    if (!options.length) return;
    if (!selectedLevel || !options.includes(selectedLevel)) {
      setSelectedLevel(options[0]);
    }
  }, [categoryLevelOptions, selectedLevel]);

  useEffect(() => {
    // Default category creation level to the currently selected level (when empty)
    if (!newCategoryLevel && selectedLevel) {
      setNewCategoryLevel(selectedLevel);
    }
  }, [newCategoryLevel, selectedLevel]);

  const filteredCategories = useMemo(() => {
    if (!selectedLevel) return Array.isArray(categories) ? categories : [];
    return (Array.isArray(categories) ? categories : []).filter((c) => String(c?.level || '') === String(selectedLevel));
  }, [categories, selectedLevel]);

  const levelLabel = (lvl) => {
    const v = (lvl || '').toString();
    if (v === 'nursery') return t('subjectManagement.add.levels.nursery');
    if (v === 'primary') return t('subjectManagement.add.levels.primary');
    if (v === 'first_cycle') return t('subjectManagement.add.levels.firstCycle');
    if (v === 'second_cycle') return t('subjectManagement.add.levels.secondCycle');
    return t('subjectManagement.add.levels.unknown', { level: v || '' });
  };

  useEffect(() => {
    fetchCategories();
    // Initialize with one empty subject
    setBatchSubjects([{ name: '', code: '', description: '' }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePhase, activePhaseId, selectedLevel]);

  useEffect(() => {
    // Clear selected category ids when level changes
    const allowedIds = new Set((filteredCategories || []).map((c) => String(c?.id)));
    setSubjectForms((prev) => (Array.isArray(prev) ? prev.map((sf) => {
      const cid = sf?.categoryId;
      if (!cid) return sf;
      return allowedIds.has(String(cid)) ? sf : { ...sf, categoryId: '' };
    }) : prev));
    setBatchFormData((prev) => {
      const cid = prev?.categoryId;
      if (!cid) return prev;
      return allowedIds.has(String(cid)) ? prev : { ...prev, categoryId: '' };
    });
  }, [filteredCategories]);

  const fetchCategories = async () => {
    try {
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseCode = resolvedPhaseCode;
      const phaseId = resolvedPhaseId ? String(resolvedPhaseId) : (localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '');
      // If phase context is missing, try to resolve it automatically (Principal/Headmaster flows)
      let effPhaseCode = phaseCode;
      let effPhaseId = phaseId;
      if (!effPhaseCode && !effPhaseId) {
        try {
          // Prefer school phases endpoint; if exactly one active, use it
          const ph = await fetchJson('/api/school/phases', { headers: schoolId ? { 'X-Scope-School-Id': schoolId } : {} });
          const rows = Array.isArray(ph?.rows) ? ph.rows : (Array.isArray(ph) ? ph : []);
          const active = rows.filter(p => p.isActive !== false);
          const chosen = (active.length === 1 ? active[0] : null) || rows[0];
          if (chosen) {
            if (chosen.code) effPhaseCode = String(chosen.code);
            if (chosen.id) effPhaseId = String(chosen.id);
          }
        } catch (_) {
          // Fallback: infer from role (Principal -> sec, Headmaster -> nur_pri)
          try {
            const me = await fetchJson('/api/users/me');
            const roleName = (me?.role || me?.roleName || me?.primaryRole?.name || '').toString().toLowerCase();
            if (roleName.includes('principal')) effPhaseCode = 'sec';
            else if (roleName.includes('head') || roleName.includes('headmaster')) effPhaseCode = 'nur_pri';
          } catch (_) {}
        }
        // Persist for subsequent requests
        try {
          if (effPhaseCode) localStorage.setItem('schoolAdmin_activePhase', effPhaseCode);
          if (effPhaseId) localStorage.setItem('schoolAdmin_activePhaseId', effPhaseId);
        } catch (_) {}
      }
      const headers = {
        ...(schoolId ? { 'X-Scope-School-Id': schoolId } : {}),
        ...((effPhaseCode || phaseCode) ? { 'X-Active-Phase': effPhaseCode || phaseCode } : {}),
        ...((effPhaseId || phaseId) ? { 'X-Active-Phase-Id': effPhaseId || phaseId } : {}),
        ...(effPhaseCode || phaseCode ? { 'X-Phase-Code': effPhaseCode || phaseCode } : {}),
        ...(effPhaseId || phaseId ? { 'X-Phase-Id': effPhaseId || phaseId } : {})
      };
      // Prefer phase+level query (level filters categories and subjects)
      const params = [];
      if (effPhaseCode || phaseCode) params.push(`phase=${encodeURIComponent(effPhaseCode || phaseCode)}`);
      if (selectedLevel) params.push(`level=${encodeURIComponent(selectedLevel)}`);
      const q = params.length ? `?${params.join('&')}` : '';
      let categoriesData = await fetchJson(`/api/subject-categories${q}`, { headers });
      // Fallbacks if empty or failing schema assumptions
      if (!Array.isArray(categoriesData)) categoriesData = Array.isArray(categoriesData?.rows) ? categoriesData.rows : (Array.isArray(categoriesData?.data) ? categoriesData.data : categoriesData);
      if (!Array.isArray(categoriesData) || categoriesData.length === 0) {
        try {
          categoriesData = await fetchJson(`/api/subject-categories${selectedLevel ? `?level=${encodeURIComponent(selectedLevel)}` : ''}`, { headers });
        } catch (_) {}
      }
      if (!Array.isArray(categoriesData) || categoriesData.length === 0) {
        try {
          categoriesData = await fetchJson(`/api/subject-categories`);
        } catch (_) {}
      }
      setCategories(categoriesData);
      try { setCategoriesOptional(!(Array.isArray(categoriesData) && categoriesData.length > 0)); } catch (_) { setCategoriesOptional(true); }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError(t('subjectManagement.add.errors.fetchCategories'));
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      const ctx = ensurePhaseContext();
      const token = localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('accessToken') || '';
      const headers = buildContextHeaders(ctx, token);

      const name = String(newCategoryName || '').trim();
      if (!name) {
        enqueueSnackbar(t('subjectManagement.add.categories.errors.nameRequired', { defaultValue: 'Category name is required' }), { variant: 'error' });
        return;
      }

      const levelValue = String(newCategoryLevel || '').trim();
      if (!levelValue) {
        enqueueSnackbar(t('subjectManagement.add.categories.errors.levelRequired', { defaultValue: 'Level is required' }), { variant: 'error' });
        return;
      }

      const payload = {
        name,
        description: String(newCategoryDescription || '').trim() || null,
        level: levelValue,
      };
      if (ctx.schoolId) payload.schoolId = Number(ctx.schoolId);
      if (Number.isInteger(ctx.phaseId)) payload.phaseId = ctx.phaseId;
      else if (ctx.phaseCode) payload.phaseCode = ctx.phaseCode;

      if (editingCategoryId) {
        await requestWithTimeout(`/api/subject-categories/${editingCategoryId}`, { method: 'PUT', headers, body: payload }, 15000);
        enqueueSnackbar(t('subjectManagement.add.categories.messages.updated', { defaultValue: 'Category updated' }), { variant: 'success' });
      } else {
        await requestWithTimeout('/api/subject-categories', { method: 'POST', headers, body: payload }, 15000);
        enqueueSnackbar(t('subjectManagement.add.categories.messages.created', { defaultValue: 'Category created' }), { variant: 'success' });
      }
      setEditingCategoryId(null);
      setNewCategoryName('');
      setNewCategoryDescription('');
      setNewCategoryLevel('');
      fetchCategories();
    } catch (err) {
      console.error('Error creating subject category:', err);
      const msg = err?.message || t('subjectManagement.add.categories.errors.defaultCreateFailed');
      setError(t('subjectManagement.add.categories.errors.createFailed', { defaultValue: 'Failed to create category: {{message}}', message: msg }));
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useScopeAutoRefresh([fetchCategories]);

  const normalizeSubjectName = (name) => {
    if (!name) return '';
    return name.toString().trim().toLowerCase();
  };

  const mapPhaseCodeToId = (code) => {
    if (!code) return null;
    const normalized = code.toString().trim().toLowerCase();
    if (normalized === 'sec' || normalized === 'secondary') return 2;
    if (normalized === 'nur_pri' || normalized === 'nursery' || normalized === 'nursery_primary') return 1;
    const parsed = parseInt(normalized, 10);
    return Number.isInteger(parsed) ? parsed : null;
  };

  const resolveCreationContext = useCallback(() => {
    const schoolId =
      localStorage.getItem('schoolId') ||
      localStorage.getItem('activeSchoolId') ||
      localStorage.getItem('scopeSchoolId') ||
      '';
    const rawPhaseCode = resolvedPhaseCode ||
      localStorage.getItem('schoolAdmin_activePhase') ||
      localStorage.getItem('activePhase') ||
      '';
    const rawPhaseId = resolvedPhaseId ??
      (typeof activePhaseId !== 'undefined' ? activePhaseId : null) ??
      localStorage.getItem('schoolAdmin_activePhaseId') ??
      localStorage.getItem('activePhaseId') ??
      '';
    const normalizedPhaseId = Number.isInteger(parseInt(rawPhaseId, 10))
      ? parseInt(rawPhaseId, 10)
      : mapPhaseCodeToId(rawPhaseCode);
    const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
    const ayId =
      localStorage.getItem(ayKey) ||
      localStorage.getItem('currentAcademicYearId') ||
      '';
    let userRole = null;
    try {
      const rawUser = localStorage.getItem('user');
      if (rawUser) {
        const parsed = JSON.parse(rawUser);
        userRole = parsed?.role || parsed?.primaryRole?.name || null;
      }
    } catch (_) {}
    return {
      schoolId,
      phaseCode: rawPhaseCode || null,
      phaseId: normalizedPhaseId,
      academicYearId: ayId ? Number(ayId) || null : null,
      userRole: userRole ? userRole.toString() : null
    };
  }, [activePhase, activePhaseId]);

  const ensurePhaseContext = () => {
    const ctx = resolveCreationContext();
    if (!ctx.phaseCode && !Number.isInteger(ctx.phaseId)) {
      const message = t('subjectManagement.add.errors.selectActivePhase');
      enqueueSnackbar(message, { variant: 'error' });
      throw new Error(message);
    }
    return ctx;
  };

  const buildContextHeaders = (ctx, token) => ({
    ...(ctx.schoolId ? { 'X-Scope-School-Id': ctx.schoolId } : {}),
    ...(ctx.phaseCode ? { 'X-Active-Phase': ctx.phaseCode } : {}),
    ...(Number.isInteger(ctx.phaseId) ? { 'X-Active-Phase-Id': ctx.phaseId } : {}),
    ...(ctx.academicYearId ? { 'X-Academic-Year-Id': ctx.academicYearId } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    'Accept': 'application/json'
  });

  const fetchExistingSubjects = useCallback(async () => {
    try {
      const ctx = resolveCreationContext();
      if (!ctx.phaseCode && !Number.isInteger(ctx.phaseId)) return;
      const headers = buildContextHeaders(ctx, null);
      const extra = [];
      if (ctx.phaseCode) extra.push(`phase=${encodeURIComponent(ctx.phaseCode)}`);
      if (ctx.schoolId) extra.push(`schoolId=${encodeURIComponent(ctx.schoolId)}`);
      const url = `/api/subjects?include=category${extra.length ? `&${extra.join('&')}` : ''}`;
      const resp = await fetchJson(url, { headers });
      const arr = Array.isArray(resp) ? resp : (resp?.subjects || resp?.rows || resp?.data || []);
      const normalized = Array.isArray(arr)
        ? arr
          .map(sub => ({
            name: normalizeSubjectName(sub?.name),
            level: String(sub?.level || '').trim().toLowerCase()
          }))
          .filter(sub => sub.name)
        : [];
      setExistingSubjects(normalized);
    } catch (error) {
      console.warn('[AddSubjectPage] Failed to fetch existing subjects', error);
    }
  }, [resolveCreationContext]);

  useEffect(() => {
    fetchExistingSubjects();
  }, [fetchExistingSubjects]);

  const existingSubjectSet = useMemo(() => new Set(
    existingSubjects.map(sub => buildSubjectLevelKey(sub?.name, sub?.level)).filter(Boolean)
  ), [existingSubjects]);

  const ensureNamesAreUnique = (names, level) => {
    const seen = new Set();
    const normalizedLevel = String(level || '').trim().toLowerCase();
    for (const name of names) {
      const normalized = normalizeSubjectName(name);
      if (!normalized) continue;
      const key = buildSubjectLevelKey(normalized, normalizedLevel);
      if (existingSubjectSet.has(key)) {
        throw new Error(t('subjectManagement.add.errors.subjectAlreadyExists', { name }));
      }
      if (seen.has(key)) {
        throw new Error(t('subjectManagement.add.errors.duplicateSubjectName', { name }));
      }
      seen.add(key);
    }
    return Array.from(seen);
  };

  const handleFormChange = (index, field, value) => {
    setSubjectForms(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addForm = () => {
    setSubjectForms(prev => [...prev, { ...EMPTY_SUBJECT_FORM }]);
  };

  const removeForm = (index) => {
    setSubjectForms(prev => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const handleBatchInputChange = (field, value) => {
    setBatchFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBatchSubjectChange = (index, field, value) => {
    const updatedSubjects = [...batchSubjects];
    updatedSubjects[index][field] = value;
    setBatchSubjects(updatedSubjects);
  };

  const addBatchSubject = () => {
    setBatchSubjects([...batchSubjects, { name: '', code: '', description: '' }]);
  };

  const removeBatchSubject = (index) => {
    if (batchSubjects.length > 1) {
      const updatedSubjects = batchSubjects.filter((_, i) => i !== index);
      setBatchSubjects(updatedSubjects);
    }
  };

  // Helper: timeout wrapper for requests to avoid infinite waits
  const requestWithTimeout = async (path, options = {}, ms = 15000) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ms);
    try {
      return await fetchJson(path, { ...options, signal: controller.signal });
    } finally {
      clearTimeout(timer);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    // Validate at least one complete row
    const valid = subjectForms.filter(f => {
      if (!String(f.name || '').trim() || !String(f.code || '').trim()) return false;
      if (isSecPhase) {
        if (showFirstCycleCoefficient) return String(f.coefficientFirstCycle || '').trim();
        if (showSecondCycleCoefficient) return String(f.coefficientSecondCycle || '').trim();
        return String(f.coefficientFirstCycle || '').trim() && String(f.coefficientSecondCycle || '').trim();
      }
      if (isNurseryLevel) return true;
      return String(f.coefficient || '').trim();
    });
    if (valid.length === 0) {
      enqueueSnackbar(t('subjectManagement.add.messages.addAtLeastOneWithCoefficient'), { variant: 'error' });
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const ctx = ensurePhaseContext();
      const token = localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('accessToken') || '';
      const headers = buildContextHeaders(ctx, token);
      ensureNamesAreUnique(valid.map(item => item.name), selectedLevel);

      for (const item of valid) {
        const payload = { ...item };
        if (isSecPhase) {
          // The coefficient for the cycle being created. The server used to build
          // BOTH variants whatever level was chosen, and this was a fallback for
          // the one nobody asked for; it now builds only what is requested, so
          // this is simply the value for the selected cycle.
          //
          // With no level selected both boxes are shown and both cycle
          // coefficients are sent, which is still the "whole of secondary" form.
          if (selectedLevel === 'first_cycle') {
            payload.coefficient = payload.coefficientFirstCycle;
          } else if (selectedLevel === 'second_cycle') {
            payload.coefficient = payload.coefficientSecondCycle;
          }
        } else {
          delete payload.coefficientFirstCycle;
          delete payload.coefficientSecondCycle;
          if (isNurseryLevel) {
            payload.coefficient = null;
          }
        }
        if (ctx.schoolId) payload.schoolId = Number(ctx.schoolId);
        if (Number.isInteger(ctx.phaseId)) payload.phaseId = ctx.phaseId;
        else if (ctx.phaseCode) payload.phaseCode = ctx.phaseCode;
        if (ctx.academicYearId) payload.academicYearId = ctx.academicYearId;
        if (selectedLevel) payload.level = selectedLevel;
        if (categoriesOptional || !payload.categoryId) delete payload.categoryId;
        // Let fetchJson set JSON headers and inject missing context into body
        await requestWithTimeout('/api/subjects', { method: 'POST', headers, body: payload }, 15000);
      }

      enqueueSnackbar(t('subjectManagement.add.messages.createdSuccess', { count: valid.length }), { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'right' } });
      setSubjectForms([{ ...EMPTY_SUBJECT_FORM }]);
      fetchExistingSubjects();
      setTimeout(() => navigate('/subject-management'), 1200);
    } catch (error) {
      console.error('Error creating subjects:', error);
      setError(t('subjectManagement.add.errors.createFailed', { message: error.message }));
      enqueueSnackbar(t('subjectManagement.add.errors.createErrorSnackbar', { message: error.message }), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleBatchSubmit = async (e) => {
    e.preventDefault();
    
    if (isSecPhase) {
      if (showFirstCycleCoefficient && !batchFormData.coefficientFirstCycle) {
        enqueueSnackbar(t('subjectManagement.add.messages.enterCoefficient'), { variant: 'error' });
        return;
      }
      if (showSecondCycleCoefficient && !batchFormData.coefficientSecondCycle) {
        enqueueSnackbar(t('subjectManagement.add.messages.enterCoefficient'), { variant: 'error' });
        return;
      }
      if (!showFirstCycleCoefficient && !showSecondCycleCoefficient && (!batchFormData.coefficientFirstCycle || !batchFormData.coefficientSecondCycle)) {
        enqueueSnackbar(t('subjectManagement.add.messages.enterCoefficient'), { variant: 'error' });
        return;
      }
    } else if (!isNurseryLevel && !batchFormData.coefficient) {
      enqueueSnackbar(t('subjectManagement.add.messages.enterCoefficient'), { variant: 'error' });
      return;
    }

    const validSubjects = batchSubjects.filter(subj => subj.name.trim() && subj.code.trim());
    if (validSubjects.length === 0) {
      enqueueSnackbar(t('subjectManagement.add.messages.addAtLeastOne'), { variant: 'error' });
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const ctx = ensurePhaseContext();
      ensureNamesAreUnique(validSubjects.map(subj => subj.name), selectedLevel);
      const subjectsToCreate = validSubjects.map(subj => {
        const base = {
          ...subj,
          ...(isSecPhase
            ? {
              coefficientFirstCycle: batchFormData.coefficientFirstCycle,
              coefficientSecondCycle: batchFormData.coefficientSecondCycle,
              // Legacy fallback: ensure server can apply same coefficient to the other cycle
              coefficient: (showFirstCycleCoefficient
                ? batchFormData.coefficientFirstCycle
                : (showSecondCycleCoefficient ? batchFormData.coefficientSecondCycle : undefined))
            }
            : { coefficient: (isNurseryLevel ? null : batchFormData.coefficient) }),
          isActive: batchFormData.isActive
        };
        if (selectedLevel) base.level = selectedLevel;
        if (Array.isArray(categories) && categories.length > 0 && batchFormData.categoryId) {
          base.categoryId = batchFormData.categoryId;
        }
        return base;
      });

      // Create subjects one by one
      const token = localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('accessToken') || '';
      const headers = {
        'Content-Type': 'application/json',
        ...buildContextHeaders(ctx, token)
      };
      for (const subjectData of subjectsToCreate) {
        if (ctx.schoolId) subjectData.schoolId = Number(ctx.schoolId);
        if (Number.isInteger(ctx.phaseId)) subjectData.phaseId = ctx.phaseId;
        else if (ctx.phaseCode) subjectData.phaseCode = ctx.phaseCode;
        if (ctx.academicYearId) subjectData.academicYearId = ctx.academicYearId;
        await fetchJson('/api/subjects', {
          method: 'POST',
          headers,
          body: JSON.stringify(subjectData)
        });
      }

      enqueueSnackbar(t('subjectManagement.add.messages.createdSuccess', { count: validSubjects.length }), { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'right' } });
      
      // Reset batch form and show success message
      setBatchFormData({
        categoryId: '',
        coefficient: '',
        coefficientFirstCycle: '',
        coefficientSecondCycle: ''
      });
      setBatchSubjects([{ name: '', code: '', description: '' }]);
      fetchExistingSubjects();
      
      // Navigate back after a short delay to show the success message
      setTimeout(() => {
        navigate('/subject-management');
      }, 1500);
      
    } catch (error) {
      console.error('Error creating subjects:', error);
      setError(t('subjectManagement.add.errors.createFailed', { message: error.message }));
      enqueueSnackbar(t('subjectManagement.add.errors.createErrorSnackbar', { message: error.message }), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/subject-management');
  };

  const handleBulkFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setBulkFile(file);
    if (event.target) event.target.value = '';
  };

  const downloadTemplate = async () => {
    try {
      setLoading(true);
      setError(null);
      const ctx = ensurePhaseContext();
      const token = localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('accessToken') || '';
      const response = await fetch(resolveApiUrl('/api/subjects/template'), {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...buildContextHeaders(ctx, null)
        },
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error(`Template download failed (${response.status})`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'subject_import_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      console.error('Error downloading subject template:', downloadError);
      const dlMsg = downloadError?.userMessage || (downloadError?.message || '').replace(/^HTTP\s+\d+[:\s]*/i, '').trim() || 'Failed to download template';
      enqueueSnackbar(dlMsg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkImport = async () => {
    if (!bulkFile) {
      enqueueSnackbar(t('subjectManagement.add.messages.addAtLeastOne'), { variant: 'error' });
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const ctx = ensurePhaseContext();
      const token = localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('accessToken') || '';
      const headers = buildContextHeaders(ctx, token);
      const form = new FormData();
      form.append('file', bulkFile);
      if (selectedLevel) form.append('level', selectedLevel);

      const data = await requestWithTimeout('/api/subjects/import?preview=1', {
        method: 'POST',
        headers,
        body: form
      }, 30000);

      const previewErrors = Array.isArray(data?.errors) ? data.errors : [];
      if (previewErrors.length) {
        throw new Error(previewErrors.map((item) => `Row ${item.row}: ${item.error}`).join(' | '));
      }

      const items = Array.isArray(data?.items) ? data.items : [];
      if (!items.length) {
        throw new Error(t('subjectManagement.add.messages.addAtLeastOne'));
      }

      const previewLevels = Array.from(new Set(items.map((item) => String(item?.level || '').trim()).filter(Boolean)));
      if (previewLevels.length > 1) {
        throw new Error(t('subjectManagement.add.bulkImport.errors.singleLevelOnly', { defaultValue: 'Imported subjects must belong to one level only. Please import one level at a time.' }));
      }

      const previewLevel = previewLevels[0] || selectedLevel || '';
      if (previewLevel) {
        setSelectedLevel(previewLevel);
      }

      setSubjectForms(items.map((item) => {
        const level = String(item?.level || previewLevel || '').trim();
        const rawCoefficient = item?.coefficient ?? '';
        let coefficientFirstCycle = item?.coefficientFirstCycle ?? '';
        let coefficientSecondCycle = item?.coefficientSecondCycle ?? '';

        if (resolvedPhaseCode === 'sec') {
          if (!coefficientFirstCycle && level === 'first_cycle' && rawCoefficient !== '') coefficientFirstCycle = rawCoefficient;
          if (!coefficientSecondCycle && level === 'second_cycle' && rawCoefficient !== '') coefficientSecondCycle = rawCoefficient;
        }

        return {
          ...EMPTY_SUBJECT_FORM,
          name: item?.name || '',
          code: item?.code || '',
          description: item?.description || '',
          categoryId: item?.categoryId ? String(item.categoryId) : '',
          coefficient: rawCoefficient,
          coefficientFirstCycle,
          coefficientSecondCycle
        };
      }));
      setBulkFile(null);
      setActiveTab(1);
      enqueueSnackbar(t('subjectManagement.add.bulkImport.messages.previewLoaded', { defaultValue: 'Import preview loaded. Review the subjects and save to confirm.', count: items.length }), { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'right' } });
    } catch (importError) {
      console.error('Error importing subjects:', importError);
      const impMsg = importError?.userMessage || (importError?.message || '').replace(/^HTTP\s+\d+[:\s]*/i, '').trim() || '';
      setError(t('subjectManagement.add.errors.createFailed', { message: impMsg }));
      enqueueSnackbar(t('subjectManagement.add.errors.createErrorSnackbar', { message: impMsg }), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleCancel}
          sx={{ mr: 2 }}
        >
          {t('common.back')}
        </Button>
        <Box>
          <Typography variant="h4" component="h1">
            {t('subjectManagement.add.pageTitle')}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {t('subjectManagement.add.subtitle')}
          </Typography>
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label={t('subjectManagement.add.tabs.categories', { defaultValue: 'Subject Categories' })} />
          <Tab label={t('subjectManagement.add.tabs.addSubjects')} />
          <Tab label={t('subjectManagement.add.tabs.bulkImport')} />
        </Tabs>
      </Paper>

      <Paper sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('subjectManagement.add.fields.level', { defaultValue: 'Level' })}</InputLabel>
              <Select
                value={selectedLevel || ''}
                label={t('subjectManagement.add.fields.level', { defaultValue: 'Level' })}
                onChange={(e) => setSelectedLevel(String(e.target.value || ''))}
              >
                {categoryLevelOptions.map((lvl) => (
                  <MenuItem key={lvl} value={lvl}>{levelLabel(lvl)}</MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {t('subjectManagement.add.helpers.levelFilter', { defaultValue: 'Used to filter categories and subjects.' })}
              </FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Single Subject Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('subjectManagement.add.sections.subjectInformation')}
              </Typography>
              
              <form onSubmit={handleCreate}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('subjectManagement.add.fields.subjectName')}
                      value={subjectForms[0]?.name || ''}
                      onChange={(e) => handleFormChange(0, 'name', e.target.value)}
                      placeholder={t('subjectManagement.add.placeholders.subjectNameMultiple')}
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('subjectManagement.add.fields.subjectCode')}
                      value={subjectForms[0]?.code || ''}
                      onChange={(e) => handleFormChange(0, 'code', e.target.value)}
                      placeholder={t('subjectManagement.add.placeholders.subjectCodeMultiple')}
                      required
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>{t('subjectManagement.add.fields.category')}</InputLabel>
                      <Select
                        value={subjectForms[0]?.categoryId || ''}
                        onChange={(e) => handleFormChange(0, 'categoryId', e.target.value)}
                        label={t('subjectManagement.add.fields.category')}
                      >
                        <MenuItem value="">
                          <em>{t('subjectManagement.add.placeholders.selectCategory')}</em>
                        </MenuItem>
                        {filteredCategories.map((category) => (
                          <MenuItem key={category.id} value={category.id}>
                            {category.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box>
                      {isSecPhase ? (
                        <Grid container spacing={1}>
                          {showFirstCycleCoefficient && (
                            <Grid item xs={12} sm={(showFirstCycleCoefficient && showSecondCycleCoefficient) ? 6 : 12}>
                              <TextField
                                fullWidth
                                type="number"
                                label={t('subjectManagement.add.fields.coefficientFirstCycle', { defaultValue: 'First Cycle Coefficient' })}
                                value={subjectForms[0]?.coefficientFirstCycle || ''}
                                onChange={(e) => handleFormChange(0, 'coefficientFirstCycle', e.target.value)}
                                placeholder={t('subjectManagement.add.placeholders.coefficientFirstCycle', { defaultValue: 'e.g. 2' })}
                                inputProps={{ min: 1, max: 5 }}
                                required
                              />
                            </Grid>
                          )}
                          {showSecondCycleCoefficient && (
                            <Grid item xs={12} sm={(showFirstCycleCoefficient && showSecondCycleCoefficient) ? 6 : 12}>
                              <TextField
                                fullWidth
                                type="number"
                                label={t('subjectManagement.add.fields.coefficientSecondCycle', { defaultValue: 'Second Cycle Coefficient' })}
                                value={subjectForms[0]?.coefficientSecondCycle || ''}
                                onChange={(e) => handleFormChange(0, 'coefficientSecondCycle', e.target.value)}
                                placeholder={t('subjectManagement.add.placeholders.coefficientSecondCycle', { defaultValue: 'e.g. 5' })}
                                inputProps={{ min: 1, max: 5 }}
                                required
                              />
                            </Grid>
                          )}
                        </Grid>
                      ) : (
                        isNurseryLevel ? (
                          <TextField
                            fullWidth
                            label={t('subjectManagement.add.fields.coefficient')}
                            value={t('subjectManagement.coefficient.hiddenForNursery')}
                            disabled
                          />
                        ) : (
                          <TextField
                            fullWidth
                            type="number"
                            label={t('subjectManagement.add.fields.coefficient')}
                            value={subjectForms[0]?.coefficient || ''}
                            onChange={(e) => handleFormChange(0, 'coefficient', e.target.value)}
                            placeholder={t('subjectManagement.add.placeholders.coefficientDefault')}
                            inputProps={{ min: 1, max: 5 }}
                            required
                          />
                        )
                      )}
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('subjectManagement.add.fields.description')}
                      value={subjectForms[0]?.description || ''}
                      onChange={(e) => handleFormChange(0, 'description', e.target.value)}
                      placeholder={t('subjectManagement.add.placeholders.description')}
                      multiline
                      rows={3}
                    />
                  </Grid>
                  
                  {/* Removed Active Subject switch (status not in DB) */}
                </Grid>
                
                {/* Multi-subject controls and additional forms */}
                <Box sx={{ my: 2 }}>
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={addForm}>
                    {t('subjectManagement.add.actions.addAnotherSubject')}
                  </Button>
                </Box>

                {subjectForms.length > 1 && (
                  <List>
                    {subjectForms.slice(1).map((sf, idx) => {
                      const index = idx + 1; // actual index in subjectForms
                      return (
                        <ListItem key={index} sx={{ border: '1px solid #e0e0e0', mb: 1, borderRadius: 1 }}>
                          <Grid container spacing={2} sx={{ flexGrow: 1 }}>
                            <Grid item xs={12} md={3}>
                              <TextField
                                fullWidth
                                label={t('subjectManagement.add.fields.subjectName')}
                                value={subjectForms[index]?.name || ''}
                                onChange={(e) => handleFormChange(index, 'name', e.target.value)}
                                required
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                fullWidth
                                label={t('subjectManagement.add.fields.subjectCode')}
                                value={subjectForms[index]?.code || ''}
                                onChange={(e) => handleFormChange(index, 'code', e.target.value)}
                                required
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                label={t('subjectManagement.add.fields.description')}
                                value={subjectForms[index]?.description || ''}
                                onChange={(e) => handleFormChange(index, 'description', e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                              <IconButton color="error" onClick={() => removeForm(index)}>
                                <DeleteIcon />
                              </IconButton>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth>
                                <InputLabel>{t('subjectManagement.add.fields.category')}</InputLabel>
                                <Select
                                  value={subjectForms[index]?.categoryId || ''}
                                  onChange={(e) => handleFormChange(index, 'categoryId', e.target.value)}
                                  label={t('subjectManagement.add.fields.category')}
                                >
                                  <MenuItem value="">
                                    <em>{t('subjectManagement.add.placeholders.selectCategory')}</em>
                                  </MenuItem>
                                  {filteredCategories.map((category) => (
                                    <MenuItem key={category.id} value={category.id}>
                                      {category.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              {isSecPhase ? (
                                <Grid container spacing={1}>
                                  {showFirstCycleCoefficient && (
                                    <Grid item xs={12} sm={(showFirstCycleCoefficient && showSecondCycleCoefficient) ? 6 : 12}>
                                      <TextField
                                        fullWidth
                                        type="number"
                                        label={t('subjectManagement.add.fields.coefficientFirstCycle', { defaultValue: 'First Cycle Coefficient' })}
                                        value={subjectForms[index]?.coefficientFirstCycle || ''}
                                        onChange={(e) => handleFormChange(index, 'coefficientFirstCycle', e.target.value)}
                                        inputProps={{ min: 1, max: 5 }}
                                        required
                                      />
                                    </Grid>
                                  )}
                                  {showSecondCycleCoefficient && (
                                    <Grid item xs={12} sm={(showFirstCycleCoefficient && showSecondCycleCoefficient) ? 6 : 12}>
                                      <TextField
                                        fullWidth
                                        type="number"
                                        label={t('subjectManagement.add.fields.coefficientSecondCycle', { defaultValue: 'Second Cycle Coefficient' })}
                                        value={subjectForms[index]?.coefficientSecondCycle || ''}
                                        onChange={(e) => handleFormChange(index, 'coefficientSecondCycle', e.target.value)}
                                        inputProps={{ min: 1, max: 5 }}
                                        required
                                      />
                                    </Grid>
                                  )}
                                </Grid>
                              ) : (
                                isNurseryLevel ? (
                                  <TextField
                                    fullWidth
                                    label={t('subjectManagement.add.fields.coefficient')}
                                    value={t('subjectManagement.coefficient.hiddenForNursery')}
                                    disabled
                                  />
                                ) : (
                                  <TextField
                                    fullWidth
                                    type="number"
                                    label={t('subjectManagement.add.fields.coefficient')}
                                    value={subjectForms[index]?.coefficient || ''}
                                    onChange={(e) => handleFormChange(index, 'coefficient', e.target.value)}
                                    placeholder={t('subjectManagement.add.placeholders.coefficientDefault')}
                                    inputProps={{ min: 1, max: 5 }}
                                    required
                                  />
                                )
                              )}
                            </Grid>
                            {/* Removed Active switch per DB model */}
                          </Grid>
                        </ListItem>
                      );
                    })}
                  </List>
                )}

                <Divider sx={{ my: 3 }} />
                
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={loading}
                  >
                    {loading ? t('subjectManagement.add.actions.creating') : t('subjectManagement.add.actions.createSubjects', { count: subjectForms.length })}
                  </Button>
                </Box>
              </form>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <SubjectIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {t('subjectManagement.add.tips.title')}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {t('subjectManagement.add.tips.bullets.clearNames')}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {t('subjectManagement.add.tips.bullets.uniqueCodes')}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {t('subjectManagement.add.tips.bullets.assignHeadsLater')}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {t('subjectManagement.add.tips.bullets.categoriesOrganize')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Batch Subjects Tab (hidden after merge to unified Add Subjects) */}
      {activeTab === 999 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('subjectManagement.add.batch.pageTitle')}
              </Typography>
              <form onSubmit={handleBatchSubmit}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('subjectManagement.add.fields.category')}</InputLabel>
                  <Select
                    value={batchFormData.categoryId}
                    onChange={(e) => handleBatchInputChange('categoryId', e.target.value)}
                    label={t('subjectManagement.add.fields.category')}
                  >
                    <MenuItem value="">
                      <em>{t('subjectManagement.add.placeholders.selectCategory')}</em>
                    </MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6} sx={{ mt: 2 }}>
                {isSecPhase ? (
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label={t('subjectManagement.add.fields.coefficientFirstCycle', { defaultValue: 'First Cycle Coefficient' })}
                        value={batchFormData.coefficientFirstCycle}
                        onChange={(e) => handleBatchInputChange('coefficientFirstCycle', e.target.value)}
                        inputProps={{ min: 1, max: 5 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label={t('subjectManagement.add.fields.coefficientSecondCycle', { defaultValue: 'Second Cycle Coefficient' })}
                        value={batchFormData.coefficientSecondCycle}
                        onChange={(e) => handleBatchInputChange('coefficientSecondCycle', e.target.value)}
                        inputProps={{ min: 1, max: 5 }}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <TextField
                    fullWidth
                    type="number"
                    label={t('subjectManagement.add.fields.coefficient')}
                    value={batchFormData.coefficient}
                    onChange={(e) => handleBatchInputChange('coefficient', e.target.value)}
                    inputProps={{ min: 1, max: 5 }}
                  />
                )}
              </Grid>

                <Box sx={{ mb: 2 }}>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={addBatchSubject}
                    variant="outlined"
                  >
                    {t('subjectManagement.add.actions.addAnotherSubject')}
                  </Button>
                </Box>

                <List>
                  {batchSubjects.map((subj, index) => (
                    <ListItem key={index} sx={{ border: '1px solid #e0e0e0', mb: 1, borderRadius: 1 }}>
                      <Grid container spacing={2} sx={{ flexGrow: 1 }}>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label={t('subjectManagement.add.fields.subjectName')}
                            value={subj.name}
                            onChange={(e) => handleBatchSubjectChange(index, 'name', e.target.value)}
                            placeholder={t('subjectManagement.add.placeholders.subjectNameSingle')}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label={t('subjectManagement.add.fields.subjectCode')}
                            value={subj.code}
                            onChange={(e) => handleBatchSubjectChange(index, 'code', e.target.value)}
                            placeholder={t('subjectManagement.add.placeholders.subjectCodeSingle')}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} md={5}>
                          <TextField
                            fullWidth
                            label={t('subjectManagement.add.fields.description')}
                            value={subj.description}
                            onChange={(e) => handleBatchSubjectChange(index, 'description', e.target.value)}
                            placeholder={t('subjectManagement.add.placeholders.briefDescription')}
                          />
                        </Grid>
                        <Grid item xs={12} md={1}>
                          <IconButton
                            onClick={() => removeBatchSubject(index)}
                            disabled={batchSubjects.length === 1}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </ListItem>
                  ))}
                </List>
                
                <Divider sx={{ my: 3 }} />
                
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={loading}
                  >
                    {loading ? t('subjectManagement.add.actions.creating') : t('subjectManagement.add.actions.createSubjects', { count: batchSubjects.filter(s => s.name.trim() && s.code.trim()).length })}
                  </Button>
                </Box>
              </form>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <SubjectIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {t('subjectManagement.add.batch.tipsTitle')}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {t('subjectManagement.add.batch.tips.bullets.sameCategory')}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {t('subjectManagement.add.batch.tips.bullets.sameStatus')}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {t('subjectManagement.add.batch.tips.bullets.assignHeadsLater')}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('subjectManagement.add.batch.tips.bullets.useBulkImport')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Bulk Import Tab */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('subjectManagement.add.bulkImport.pageTitle')}
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Button
                  startIcon={<DownloadIcon />}
                  onClick={downloadTemplate}
                  variant="outlined"
                  sx={{ mr: 2 }}
                >
                  {t('subjectManagement.add.bulkImport.actions.downloadTemplate')}
                </Button>
                <Button
                  startIcon={<UploadIcon />}
                  variant="contained"
                  component="label"
                >
                  {t('subjectManagement.add.bulkImport.actions.uploadExcelFile')}
                  <input type="file" hidden accept=".xlsx,.xls,.csv" onChange={handleBulkFileChange} />
                </Button>
              </Box>

              {bulkFile && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {bulkFile.name}
                </Alert>
              )}

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>{t('subjectManagement.add.bulkImport.guidelines.title')}</strong>
                </Typography>
                                 <Typography variant="body2" component="div">
                   {t('subjectManagement.add.bulkImport.guidelines.items.downloadTemplate')}
                   {t('subjectManagement.add.bulkImport.guidelines.items.requiredFields')}
                   {t('subjectManagement.add.bulkImport.guidelines.items.coefficientRange')}
                   {t('subjectManagement.add.bulkImport.guidelines.items.sameCategory')}
                   {t('subjectManagement.add.bulkImport.guidelines.items.maxPerImport')}
                   {t('subjectManagement.add.bulkImport.guidelines.items.supportedFormats')}
                 </Typography>
              </Alert>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  disabled={loading || !bulkFile}
                  onClick={handleBulkImport}
                >
                  {t('subjectManagement.add.bulkImport.actions.importSubjects')}
                </Button>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <UploadIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {t('subjectManagement.add.bulkImport.sideCardTitle')}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {t('subjectManagement.add.bulkImport.sideGuidelines.templateFormat')}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {t('subjectManagement.add.bulkImport.sideGuidelines.uniqueCodes')}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {t('subjectManagement.add.bulkImport.sideGuidelines.avoidSpecialChars')}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('subjectManagement.add.bulkImport.sideGuidelines.contactSupport')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Subject Categories Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {editingCategoryId
                  ? t('subjectManagement.add.categories.editTitle', { defaultValue: 'Edit Subject Category' })
                  : t('subjectManagement.add.categories.title', { defaultValue: 'Create Subject Category' })}
              </Typography>
              <form onSubmit={handleCreateCategory}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('subjectManagement.add.categories.fields.name', { defaultValue: 'Category Name' })}
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel id="category-level-label">
                        {t('subjectManagement.add.categories.fields.level', { defaultValue: 'Level' })}
                      </InputLabel>
                      <Select
                        labelId="category-level-label"
                        label={t('subjectManagement.add.categories.fields.level', { defaultValue: 'Level' })}
                        value={newCategoryLevel}
                        onChange={(e) => setNewCategoryLevel(e.target.value)}
                      >
                        <MenuItem value=""><em>{t('common.select')}</em></MenuItem>
                        {categoryLevelOptions.map((lvl) => (
                          <MenuItem key={lvl} value={lvl}>{levelLabel(lvl)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('subjectManagement.add.categories.fields.description', { defaultValue: 'Description (optional)' })}
                      value={newCategoryDescription}
                      onChange={(e) => setNewCategoryDescription(e.target.value)}
                      multiline
                      rows={3}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={() => {
                      setEditingCategoryId(null);
                      setNewCategoryName('');
                      setNewCategoryDescription('');
                      setNewCategoryLevel('');
                    }}
                    disabled={loading}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={loading}
                  >
                    {loading
                      ? (editingCategoryId
                        ? t('subjectManagement.add.categories.actions.updating', { defaultValue: 'Updating…' })
                        : t('subjectManagement.add.categories.actions.creating', { defaultValue: 'Creating…' }))
                      : (editingCategoryId
                        ? t('subjectManagement.add.categories.actions.update', { defaultValue: 'Update Category' })
                        : t('subjectManagement.add.categories.actions.create', { defaultValue: 'Create Category' }))}
                  </Button>
                </Box>
              </form>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('subjectManagement.add.categories.sideTitle', { defaultValue: 'Existing Categories' })}
                </Typography>
                {(categories || []).length === 0 ? (
                  <Alert severity="info">
                    {t('subjectManagement.add.categories.none', { defaultValue: 'No categories found for this phase yet.' })}
                  </Alert>
                ) : (
                  <List dense>
                    {(categories || []).slice(0, 20).map((c) => (
                      <ListItem key={c.id}>
                        <Typography variant="body2">
                          {c.name}
                          {c.level ? ` (${levelLabel(c.level)})` : ''}
                        </Typography>
                      </ListItem>
                    ))}
                    {(categories || []).length > 20 && (
                      <ListItem>
                        <Typography variant="caption" color="text.secondary">
                          {t('subjectManagement.add.categories.more', { defaultValue: 'More…' })}
                        </Typography>
                      </ListItem>
                    )}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default AddSubjectPage;
